import React from "react";
import {
  BarChart3,
  Zap,
  TrendingUp,
  Target,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  PieChart,
  Megaphone,
  LineChart,
  Repeat,
  Building2,
  Users2,
  GanttChartSquare
} from "lucide-react";
import { motion } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Navbar from "../Components/Navbar";
import Call from "../Components/Call";
import Footer from "../Components/Footer";
import ChatbotWidget from "./ChatComponent";
import TrustStatement from "../Components/TrustStatement";
import "../Styling/MK.css";

gsap.registerPlugin(ScrollTrigger);

const containerVariants = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0,
      delayChildren: 0
    }
  }
};

const itemVariants = {
  hidden: { opacity: 1, y: 0 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0 }
  }
};

export default function MK() {
  const [currentFeature, setCurrentFeature] = React.useState(0);
  const totalFeatures = 3;
  const mainRef = React.useRef(null);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % totalFeatures);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  React.useEffect(() => {
    const ctx = gsap.context((self) => {
      // Floating Animation for Cards
      gsap.to(".main-card", {
        y: -20,
        duration: 2.5,
        repeat: -1,
        yoyo: true,
        ease: "power1.inOut"
      });

      gsap.to(".card-1", {
        y: 15,
        x: 10,
        duration: 3,
        repeat: -1,
        yoyo: true,
        ease: "power1.inOut"
      });

      gsap.to(".card-2", {
        y: -15,
        x: -10,
        duration: 3.5,
        repeat: -1,
        yoyo: true,
        ease: "power1.inOut"
      });

      // Background Blobs
      gsap.to(".MK-blob", {
        y: "30%",
        rotation: 10,
        duration: 8,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
        stagger: {
          each: 2,
          from: "random"
        }
      });

      // Mesh Overlay Rotation
      gsap.to(".MK-mesh-overlay", {
        rotation: 360,
        duration: 120,
        repeat: -1,
        ease: "linear"
      });

      ScrollTrigger.refresh();
    }, mainRef);

    const refreshTimer = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 500);

    return () => {
      ctx.revert();
      clearTimeout(refreshTimer);
    };
  }, []);

  const nextFeature = () => setCurrentFeature((prev) => (prev + 1) % totalFeatures);
  const prevFeature = () => setCurrentFeature((prev) => (prev === 0 ? totalFeatures - 1 : prev - 1));
  const goToFeature = (index) => setCurrentFeature(index);

  return (
    <div className="MK-page-wrapper" ref={mainRef}>
      <Navbar />

      {/* HERO SECTION (CINEMATIC) */}
      <section className="MK-header-section">
        {/* Background Effects */}
        <div className="MK-mesh-container">
          <div className="MK-mesh-overlay"></div>
        </div>
        <div className="MK-blob blob-teal"></div>
        <div className="MK-blob blob-green"></div>
        <div className="MK-blob blob-cyan"></div>

        <div className="container">
          <div className="MK-hero-layout">
            <motion.div
              className="MK-hero-content"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.div className="MK-header-badge" variants={itemVariants}>
                <Megaphone size={16} />
                <span>Growth Architecture</span>
              </motion.div>

              <motion.h1 className="MK-header-title" variants={itemVariants}>
                Automated Growth Systems Built for <span className="teal-gradient">Performance</span>
              </motion.h1>

              <motion.p className="MK-header-desc" variants={itemVariants}>
                We engineer marketing systems focused on attribution, automation, and scalable performance. Replace manual campaign management with measurable, repeatable growth infrastructure.
              </motion.p>

              <motion.div className="MK-hero-ctas" variants={itemVariants}>
                <a
                  href="https://studio.digitaldreamworksagency.com/access"
                  className="MK-btn-hero"
                  onClick={() => {
                    if (window.fbq) {
                      window.fbq('trackCustom', 'RequestDemoOpened', {
                        source: 'mk_hero',
                        page_title: 'Marketing Systems (MK)'
                      });
                    }
                  }}
                >
                  Request Demo Access <ArrowRight size={18} />
                </a>
                <div className="MK-trust">
                  <TrendingUp size={16} color="#2dd4bf" />
                  <span>Scalable ROAS</span>
                </div>
              </motion.div>
            </motion.div>

            <div className="MK-hero-visual">
              <div className="visual-card main-card">
                <BarChart3 size={64} strokeWidth={1.5} />
                <span>Analytics</span>
              </div>
              <div className="visual-card secondary-card card-1">
                <Target size={32} color="#2dd4bf" />
                <span>Targeting</span>
              </div>
              <div className="visual-card secondary-card card-2">
                <Repeat size={32} color="#10b981" />
                <span>Loops</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SLIDER SECTION */}
      <section className="MK-slider-section">
        <div className="container">
          <div className="MK-slider-header">
            <h2 className="MK-slider-title">Growth Infrastructure</h2>
            <p className="MK-slider-subtitle">Continuous performance layers designed to scale acquisition.</p>
          </div>

          <div className="MK-carousel">
            <div className="MK-carousel-viewport">
              {[
                {
                  title: "Funnel Intelligence",
                  desc: "Automated systems that optimize user journeys and conversion paths.",
                  label: "Smart Funnels",
                  icon: <Repeat size={42} />
                },
                {
                  title: "Creative Automation",
                  desc: "AI‑powered creative generation with performance feedback loops.",
                  label: "Dynamic Content",
                  icon: <Target size={42} />
                },
                {
                  title: "Attribution & Measurement",
                  desc: "Clear performance tracking across channels and funnels.",
                  label: "Total Visibility",
                  icon: <LineChart size={42} />
                }
              ].map((slide, i) => (
                <div key={i} className={`MK-carousel-slide ${currentFeature === i ? "is-active" : ""}`}>
                  <div className="MK-slide-inner">
                    <div className="MK-slide-icon">{slide.icon}</div>
                    <h3 className="MK-slide-heading">{slide.title}</h3>
                    <p className="MK-slide-text">{slide.desc}</p>
                    <div className="MK-slide-meta">{slide.label}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="MK-carousel-navigation">
              <button className="MK-nav-arrow" onClick={prevFeature}><ChevronLeft size={28} /></button>
              <div className="MK-nav-pagination">
                {[0, 1, 2].map(i => (
                  <button key={i} className={`MK-page-dot ${currentFeature === i ? "is-active" : ""}`} onClick={() => goToFeature(i)} />
                ))}
              </div>
              <button className="MK-nav-arrow" onClick={nextFeature}><ChevronRight size={28} /></button>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT THIS SYSTEM SOLVES */}
      <section className="MK-solves-section">
        <div className="container">
          <div className="MK-solves-box">
            <div className="solves-header">
              <h2 className="solves-title">What This System Solves</h2>
              <p className="solves-lead">Eliminating inefficiency and scaling bottlenecks.</p>
            </div>
            <div className="solves-list-grid">
              {[
                { text: "Manual campaign management", icon: <Repeat size={20} /> },
                { text: "Unclear attribution", icon: <LineChart size={20} /> },
                { text: "Inconsistent creative performance", icon: <Target size={20} /> },
                { text: "Scaling limitations", icon: <TrendingUp size={20} /> }
              ].map((item, i) => (
                <div key={i} className="solves-item">
                  <div className="solves-icon-circle">{item.icon}</div>
                  <span className="solves-text">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE BUILD */}
      <section className="MK-capabilities-section">
        <div className="container">
          <div className="MK-section-header centered">
            <span className="MK-section-badge">Capabilities</span>
            <h2 className="MK-section-title">What We Build</h2>
            <p className="MK-section-subtitle">Scalable infrastructure designed for high-performance marketing automation.</p>
          </div>
          <div className="MK-capabilities-grid">
            {[
              {
                title: "Funnel Systems",
                desc: "End-to-end funnel optimization systems that turn traffic into repeatable revenue.",
                icon: <PieChart size={24} color="white" />,
                colorClass: "teal"
              },
              {
                title: "ROAS Dashboards",
                desc: "Real-time analytics and attribution dashboards for complete performance clarity.",
                icon: <BarChart3 size={24} color="white" />,
                colorClass: "green"
              },
              {
                title: "Creative Pipelines",
                desc: "Automated creative production pipelines matched to audience performance data.",
                icon: <Zap size={24} color="white" />,
                colorClass: "lime"
              },
              {
                title: "CRO Frameworks",
                desc: "Systematic experimentation and conversion frameworks built into your growth engine.",
                icon: <Target size={24} color="white" />,
                colorClass: "emerald"
              }
            ].map((cap, i) => (
              <div key={i} className="MK-capability-card">
                <div className="card-top">
                  <div className={`MK-capability-icon ${cap.colorClass}`}>
                    {cap.icon}
                  </div>
                  <h3 className="MK-capability-title">{cap.title}</h3>
                </div>
                <p className="CE-capability-desc">{cap.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IDEAL FOR */}
      <section className="MK-ideal-section">
        <div className="container">
          <div className="MK-section-header centered">
            <h2 className="MK-section-title">Ideal For</h2>
            <p className="MK-section-subtitle">Growth infrastructure built for teams that demand precision and scalability.</p>
          </div>
          <div className="MK-ideal-grid">
            {[
              {
                title: "Scaling Agencies",
                desc: "Agencies scaling performance offers without linear cost increases.",
                icon: <GanttChartSquare />
              },
              {
                title: "D2C & B2B Brands",
                desc: "Brands needing predictable, automated acquisition channels.",
                icon: <Building2 />
              },
              {
                title: "Enterprises",
                desc: "Large teams demanding attribution clarity and performance precision.",
                icon: <Users2 />
              }
            ].map((item, i) => (
              <div className="MK-ideal-card" key={i}>
                <div className="MK-ideal-icon">{item.icon}</div>
                <h4 className="MK-ideal-h4">{item.title}</h4>
                <p className="MK-ideal-p">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ENGAGEMENT MODEL */}
      <section className="MK-engagement-section">
        <div className="container">
          <h2 className="MK-engagement-heading">Engagement Model</h2>
          <div className="MK-steps-container">
            {[
              { title: "Evaluation", desc: "Performance audit and attribution scope." },
              { title: "System Demos", desc: "Experience the architecture with demo data." },
              { title: "Deployment", desc: "Custom rollout of your growth infrastructure." },
              { title: "Long-term Support", desc: "Ongoing licensing and roadmap alignment." }
            ].map((step, i) => (
              <div className="MK-step" key={i}>
                <div className="step-number-container">
                  <div className="MK-step-number">{i + 1}</div>
                  {i < 3 && <div className="MK-step-connector"></div>}
                </div>
                <h4 className="MK-step-h4">{step.title}</h4>
                <p className="MK-step-p">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <TrustStatement />

      <div className="LD-divider"></div>
      <ChatbotWidget />
      <Call />
      <Footer />
    </div>
  );
}
